import { useState } from 'react'
import { notifications } from '@mantine/notifications'
import {
  type RegistroMedicaoCreateInput,
  useMedicaoCreate,
  useMedicaoList,
} from '../../hooks/useMedicoes'
import { extractApiError } from '../../lib/extractApiError'

interface Props {
  equipamentoId: string
}

export default function MedicoesSection({ equipamentoId }: Props) {
  const lista = useMedicaoList({ equipamento: equipamentoId })
  const create = useMedicaoCreate()
  const [aberto, setAberto] = useState(false)
  const [form, setForm] = useState<RegistroMedicaoCreateInput>({
    equipamento: equipamentoId,
    data_medicao: new Date().toISOString().slice(0, 16),
    temperatura_saida_ar: '',
    temperatura_retorno_ar: '',
    pressao_alta: '',
    pressao_baixa: '',
    corrente_compressor: '',
    tensao: '',
    observacoes: '',
  })

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    try {
      await create.mutateAsync(form)
      notifications.show({ color: 'green', message: 'Medição registrada.' })
      setForm({
        equipamento: equipamentoId,
        data_medicao: new Date().toISOString().slice(0, 16),
        temperatura_saida_ar: '',
        temperatura_retorno_ar: '',
        pressao_alta: '',
        pressao_baixa: '',
        corrente_compressor: '',
        tensao: '',
        observacoes: '',
      })
      setAberto(false)
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao salvar medição.'),
      })
    }
  }

  return (
    <section
      style={{
        background: 'white',
        padding: '1.25rem',
        borderRadius: 8,
        marginTop: '1rem',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
      data-testid="medicoes-section"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1rem' }}>
          Medições mecânicas ({lista.data?.results.length ?? 0})
        </h2>
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          data-testid="medicao-toggle-form"
          style={{
            padding: '0.4rem 0.8rem',
            background: aberto ? '#888' : '#1565C0',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: '0.85rem',
          }}
        >
          {aberto ? '× Cancelar' : '+ Nova medição'}
        </button>
      </div>
      <p style={{ fontSize: '0.8rem', color: '#666', margin: '0 0 0.5rem' }}>
        Append-only — medições são imutáveis após criação (evidência técnica).
        Delta-T calculado automaticamente.
      </p>

      {aberto && (
        <form
          onSubmit={handleCreate}
          style={{
            padding: '0.75rem',
            background: '#FAFAFA',
            borderRadius: 4,
            marginBottom: '1rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.5rem',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2', fontSize: '0.8rem' }}>
            Data/hora medição *
            <input
              type="datetime-local"
              data-testid="medicao-data"
              value={form.data_medicao}
              onChange={(e) => setForm({ ...form, data_medicao: e.target.value })}
              required
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Temp. retorno (°C)
            <input
              type="number"
              step="0.1"
              data-testid="medicao-temp-retorno"
              value={form.temperatura_retorno_ar ?? ''}
              onChange={(e) => setForm({ ...form, temperatura_retorno_ar: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Temp. insuflamento (°C)
            <input
              type="number"
              step="0.1"
              data-testid="medicao-temp-saida"
              value={form.temperatura_saida_ar ?? ''}
              onChange={(e) => setForm({ ...form, temperatura_saida_ar: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Pressão alta (psi)
            <input
              type="number"
              step="0.1"
              value={form.pressao_alta ?? ''}
              onChange={(e) => setForm({ ...form, pressao_alta: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Pressão baixa (psi)
            <input
              type="number"
              step="0.1"
              value={form.pressao_baixa ?? ''}
              onChange={(e) => setForm({ ...form, pressao_baixa: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Corrente compressor (A)
            <input
              type="number"
              step="0.01"
              value={form.corrente_compressor ?? ''}
              onChange={(e) => setForm({ ...form, corrente_compressor: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.8rem' }}>
            Tensão (V)
            <input
              type="number"
              step="0.1"
              value={form.tensao ?? ''}
              onChange={(e) => setForm({ ...form, tensao: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 4', fontSize: '0.8rem' }}>
            Observações
            <textarea
              rows={2}
              value={form.observacoes ?? ''}
              onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
              style={{ padding: '0.4rem' }}
            />
          </label>
          <button
            type="submit"
            disabled={create.isPending}
            data-testid="medicao-submit"
            style={{
              gridColumn: 'span 4',
              padding: '0.5rem 1rem',
              background: '#2E7D32',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {create.isPending ? 'Salvando...' : 'Registrar medição'}
          </button>
        </form>
      )}

      {lista.isLoading ? (
        <p style={{ color: '#666' }}>Carregando medições...</p>
      ) : (lista.data?.results.length ?? 0) === 0 ? (
        <p style={{ color: '#666', margin: 0 }}>Nenhuma medição registrada para este equipamento.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ background: '#F5F5F5' }}>
              <th style={{ padding: '0.4rem', textAlign: 'left' }}>Data</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>T retorno (°C)</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>T insufl. (°C)</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>ΔT (°C)</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>P alta</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>P baixa</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>Amp.</th>
              <th style={{ padding: '0.4rem', textAlign: 'left' }}>Técnico</th>
            </tr>
          </thead>
          <tbody>
            {lista.data?.results.map((m) => (
              <tr key={m.id} style={{ borderTop: '1px solid #EEE' }}>
                <td style={{ padding: '0.4rem' }}>
                  {new Date(m.data_medicao).toLocaleString('pt-BR')}
                </td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  {m.temperatura_retorno_ar ?? '—'}
                </td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  {m.temperatura_saida_ar ?? '—'}
                </td>
                <td
                  style={{
                    padding: '0.4rem',
                    textAlign: 'right',
                    fontWeight: 600,
                    color:
                      m.delta_t && parseFloat(m.delta_t) >= 8 ? '#2E7D32' : '#C62828',
                  }}
                >
                  {m.delta_t ?? '—'}
                </td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{m.pressao_alta ?? '—'}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>{m.pressao_baixa ?? '—'}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  {m.corrente_compressor ?? '—'}
                </td>
                <td style={{ padding: '0.4rem', color: '#666' }}>{m.tecnico_str ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
